import React, { useState, useEffect, useCallback, createContext, useRef } from 'react';
import cn from 'classnames';
import moment from 'moment';
import './Office.scss';

import {
  getOfficeFloors,
  getOfficeSpaceAvailability,
  getMyOfficeBookings,
  getOfficeFloorZoomParticipants,
  getOfficeTeamMembers,
  getOfficeAnnouncements
} from '../../api/office';
import { canManageOffice, POLL_INTERVAL_MS, TIME_RANGE, formatDayLabel, formatDayPossessive } from '../../helpers/office_helpers';
import OfficeTab from './OfficeTab';
import OfficeSpaceBuilder from './OfficeSpaceBuilder';
import OfficeMyBookings from './OfficeMyBookings';
import OfficeAnnouncements from './OfficeAnnouncements';
import OfficeTeam from './OfficeTeam';

/*
 * OfficeContext provides shared state to all Office sub-components:
 * - Floor/space selection and availability data
 * - Date/time picker state (selectedDate, selectedTime, selectedEndTime)
 * - Person highlight system (focusedUserId, schedule badges, floor tab hints)
 * - Booking lifecycle (onBookingChange triggers availability + bookings + roster refresh)
 * - Current user info and admin permissions
 */
export const OfficeContext = createContext(null);

const TABS = { OFFICE: 'office', MY_BOOKINGS: 'my_bookings', ANNOUNCEMENTS: 'announcements', TEAM: 'team', MANAGE: 'manage' };

const STORAGE_KEYS = {
  FLOOR_ID: 'office_selected_floor_id',
  VIEW_MODE: 'office_view_mode',
  ACTIVE_TAB: 'office_active_tab'
};

const readStorage = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

/*
 * Office — full-screen app shell for the booking system.
 *
 * Data flow:
 *   1. loadInitialData() fetches floors, restores last-selected floor from localStorage
 *   2. loadAvailability() fetches spaces + bookings for the selected floor/date (re-runs on floor or date change)
 *   3. 10-second polling interval keeps availability fresh without websockets
 *   4. onBookingChange() is the unified refresh callback — called after any create/cancel to sync all views
 *
 * Loading screen:
 *   Shows "Entering the office..." with a minimum 0.5s display, then fades out over 0.6s.
 *   Covers the initial data fetch so the UI never flashes empty state.
 */
const Office = props => {
  const { currentUser } = props;
  const isAdmin = canManageOffice(currentUser);

  useEffect(() => {
    document.title = 'ShopMy Office';
  }, []);

  const [floors, setFloors] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  const [activeTab, setActiveTab] = useState(readStorage(STORAGE_KEYS.ACTIVE_TAB, TABS.OFFICE));
  const [viewMode, setViewMode] = useState(readStorage(STORAGE_KEYS.VIEW_MODE, 'map'));
  const storedFloorId = useRef(readStorage(STORAGE_KEYS.FLOOR_ID, null));
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [zoomParticipants, setZoomParticipants] = useState(null);
  const [myBookings, setMyBookings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(true);
  const [selectedDateAnnouncement, setSelectedDateAnnouncement] = useState(undefined);
  const [highlightedUserId, setHighlightedUserId] = useState(null);
  const [lockedUserId, setLockedUserId] = useState(null);
  const focusedUserId = lockedUserId || highlightedUserId;
  const [focusedUserFloorIds, setFocusedUserFloorIds] = useState([]);
  const [focusedUserSchedule, setFocusedUserSchedule] = useState([]);
  const [bookingVersion, setBookingVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const now = new Date();
  const clampedHour = Math.max(TIME_RANGE.start, Math.min(now.getHours(), TIME_RANGE.end - 1));
  const snappedMinute = now.getMinutes() < 30 ? '00' : '30';
  const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedTime, setSelectedTime] = useState(`${String(clampedHour).padStart(2, '0')}:${snappedMinute}`);
  const [selectedEndTime, setSelectedEndTime] = useState(null);

  const selectedFloor = floors.find(f => f.id === selectedFloorId);

  const changeDate = useCallback(
    newDate => {
      setSelectedDate(newDate);
      setSelectedDateAnnouncement(announcements.find(a => a.date === newDate));
    },
    [announcements, setSelectedDate, setSelectedDateAnnouncement]
  );

  const refreshMyBookings = useCallback(async () => {
    try {
      const resp = await getMyOfficeBookings();
      setMyBookings(resp?.bookings || []);
    } catch (e) {
      console.error('Failed to load bookings', e);
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const resp = await getOfficeAnnouncements();
      let data = Array.isArray(resp) ? resp : resp?.data || [];

      // Filter today or future
      const today = moment().format('YYYY-MM-DD');
      data = data.map(a => ({ ...a, date: moment.utc(a.date).format('YYYY-MM-DD') })).filter(a => a.date >= today);

      setAnnouncements(data);
      setSelectedDateAnnouncement(data.find(a => a.date === selectedDate));
    } catch {
      window.ALERT.error('Failed to load announcements.');
    } finally {
      setAnnouncementsLoading(false);
    }
  }, []);

  const myBookingsCount = myBookings.length;

  const loadTeamUsers = async () => {
    try {
      const resp = await getOfficeTeamMembers();
      setAllUsers(resp?.members || []);
    } catch (e) {
      console.error('Failed to load team members', e);
    }
  };

  useEffect(() => {
    loadInitialData();
    refreshMyBookings();
    loadTeamUsers();
    fetchAnnouncements();
  }, []);

  const loadInitialData = async () => {
    try {
      const floorsResp = await getOfficeFloors();
      const floorsData = Array.isArray(floorsResp) ? floorsResp : [];
      setFloors(floorsData);
      if (floorsData.length) {
        const stored = storedFloorId.current;
        const restoredFloor = stored ? floorsData.find(f => String(f.id) === stored) : null;
        const floor9 = floorsData.find(f => f.floor === 9);
        setSelectedFloorId(restoredFloor?.id || floor9?.id || floorsData[0].id);
      }
    } catch {
      window.ALERT.error('Failed to load office data.');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshFloors = useCallback(async () => {
    try {
      const floorsResp = await getOfficeFloors();
      setFloors(Array.isArray(floorsResp) ? floorsResp : []);
    } catch {
      /* silent */
    }
  }, []);

  const loadFloorZoomParticipants = useCallback(async () => {
    try {
      const resp = await getOfficeFloorZoomParticipants(selectedFloorId);
      setZoomParticipants(resp?.participants);
    } catch {
      /* silent */
    }
  }, [selectedFloorId]);

  useEffect(() => {
    if (!selectedFloorId) return;
    loadAvailability();
    loadFloorZoomParticipants();
  }, [selectedFloorId, selectedDate, selectedTime]);

  const loadAvailability = useCallback(async () => {
    if (!selectedFloorId) return;
    const starts_at = `${selectedDate}T07:00:00`;
    const ends_at = `${selectedDate}T21:00:00`;
    try {
      const resp = await getOfficeSpaceAvailability({ OfficeFloor_id: selectedFloorId, starts_at, ends_at });
      setSpaces(resp?.spaces || []);
    } catch (err) {
      console.error('Failed to load availability', err);
    }
  }, [selectedFloorId, selectedDate]);

  const onBookingChange = useCallback(async () => {
    await loadAvailability();
    refreshMyBookings();
    setBookingVersion(v => v + 1);
  }, [loadAvailability, refreshMyBookings]);

  useEffect(() => {
    if (!selectedFloorId) return;
    const interval = setInterval(() => {
      onBookingChange();
      loadFloorZoomParticipants();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [selectedFloorId, selectedDate, onBookingChange, loadFloorZoomParticipants]);

  useEffect(() => {
    if (selectedFloorId) localStorage.setItem(STORAGE_KEYS.FLOOR_ID, String(selectedFloorId));
  }, [selectedFloorId]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.VIEW_MODE, viewMode);
  }, [viewMode]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.ACTIVE_TAB, activeTab);
  }, [activeTab]);

  const selectedSpace = spaces.find(s => s.id === selectedSpaceId) || null;
  const setSelectedSpace = space => setSelectedSpaceId(space?.id || null);

  const selectedDayLabel = formatDayLabel(selectedDate);
  const selectedDayPossessive = formatDayPossessive(selectedDate);

  const contextValue = {
    floors,
    spaces,
    allUsers,
    loadTeamUsers,
    selectedFloor,
    selectedFloorId,
    setSelectedFloorId,
    selectedDate,
    setSelectedDate: changeDate,
    selectedDateAnnouncement,
    selectedTime,
    setSelectedTime,
    selectedEndTime,
    setSelectedEndTime,
    selectedDayLabel,
    selectedDayPossessive,
    selectedSpace,
    setSelectedSpace,
    zoomParticipants,
    viewMode,
    setViewMode,
    isAdmin,
    currentUser,
    loadAvailability,
    refreshFloors,
    myBookings,
    myBookingsCount,
    refreshMyBookings,
    announcements,
    announcementsLoading,
    fetchAnnouncements,
    onBookingChange,
    bookingVersion,
    focusedUserId,
    setHighlightedUserId,
    lockedUserId,
    setLockedUserId,
    focusedUserFloorIds,
    setFocusedUserFloorIds,
    focusedUserSchedule,
    setFocusedUserSchedule
  };

  const [showLoading, setShowLoading] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    const minDisplayTimer = setTimeout(() => {
      setFadingOut(true);
      setTimeout(() => setShowLoading(false), 600);
    }, 500);
    return () => clearTimeout(minDisplayTimer);
  }, [isLoading]);

  // Tab config — Manage tab is only visible to office_admin users
  const tabs = [
    { key: TABS.OFFICE, label: 'Office' },
    { key: TABS.MY_BOOKINGS, label: 'My Bookings', count: myBookingsCount },
    { key: TABS.ANNOUNCEMENTS, label: 'Announcements' },
    { key: TABS.TEAM, label: 'Team' },
    ...(isAdmin ? [{ key: TABS.MANAGE, label: 'Manage' }] : [])
  ];

  return (
    <OfficeContext.Provider value={contextValue}>
      {showLoading && (
        <div className={cn('office-loading-screen', { fading: fadingOut })}>
          <div className='office-loading-content'>
            <div className='office-loading-logo'>SHOPMY</div>
            <div className='office-loading-text'>Entering the office</div>
          </div>
        </div>
      )}

      <div className='office-outer'>
        <div className='office-nav'>
          <div className='office-nav-brand'>Home Away from Home</div>
          <div className='office-nav-right'>
            <div className='office-nav-tabs'>
              {tabs.map(tab => (
                <button key={tab.key} className={cn('office-nav-tab', { active: activeTab === tab.key })} onClick={() => setActiveTab(tab.key)}>
                  {tab.label}
                  {tab.count > 0 && <span className='tab-count'>{tab.count}</span>}
                </button>
              ))}
            </div>
            {currentUser?.name && <span className='office-nav-username'>{currentUser.name}</span>}
          </div>
        </div>

        <div className='office-content'>
          {activeTab === TABS.OFFICE && <OfficeTab />}
          {activeTab === TABS.MY_BOOKINGS && <OfficeMyBookings />}
          {activeTab === TABS.ANNOUNCEMENTS && <OfficeAnnouncements />}
          {activeTab === TABS.TEAM && <OfficeTeam />}
          {activeTab === TABS.MANAGE && isAdmin && <OfficeSpaceBuilder />}
        </div>
      </div>
    </OfficeContext.Provider>
  );
};

export default Office;
